import React, { useRef, useState } from 'react'
import classnames from 'classnames'
import { Button } from '../../../../components/Button/Button'
import { Form } from 'react-bootstrap'
import { downloadFile, useLazyDownloadGlobalFileRequestQuery, useUploadFileMutation, useUploadFileRequestMutation } from '../../../../store/media/file'
import { Nullable } from '../../../../common/Nullable'

import { useAppSelector } from '../../../../store/hooks'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { humanFileSize } from '../../../../common/util'
import { ComplianceFileInfo } from '../../../../models/Account'
import { FileKind, FileStatus } from '../../../../models/File'
import { useUpdateFileMutation } from '../../../../store/media/media'
import { MissingScopeAction } from '../../../../components/ScopedBlock/models'
import { ScopedBlock } from '../../../../components/ScopedBlock/ScopedBlock'

export type ComplianceFileType = 'image' | 'audio'
export type ComplianceType = 'default' | 'in-person'

export type ComplianceNoticeFileProps = {
  fileType: ComplianceFileType
  downloadFileCallback: (file: Nullable<Blob>, fileType: ComplianceFileType) => void
  uploadFileCallback: (fileId: string, fileType: ComplianceFileType, type: ComplianceType) => void
  resetFileCallback: (fileType: ComplianceFileType, type: ComplianceType) => void
  complianceFileInfo: Nullable<ComplianceFileInfo>
  maxFileSize: number
  buttonBackgroundImage: string
  supportedMimeTypes: string
  uploadEnabled: boolean
  restoreEnabled: boolean
  isLoading?: boolean
  type: ComplianceType
}

export const ComplianceNoticeFileForm: React.FC<ComplianceNoticeFileProps> = ({
  fileType,
  downloadFileCallback,
  uploadFileCallback,
  complianceFileInfo,
  maxFileSize,
  buttonBackgroundImage,
  supportedMimeTypes,
  resetFileCallback,
  uploadEnabled,
  restoreEnabled,
  isLoading,
  type,
}) => {
  const accountId = useAppSelector(selectAccountId)
  const [errorMessage, setErrorMessage] = useState('')

  const fileInputRef = useRef<HTMLInputElement>(null)

  const [downloadFileRequest] = useLazyDownloadGlobalFileRequestQuery()
  const [uploadFileRequest, { isLoading: uploadFileRequestIsLoading }] = useUploadFileRequestMutation()
  const [uploadFile, { isLoading: uploadFileIsLoading }] = useUploadFileMutation()
  const [updateFile, { isLoading: updateFileIsLoading }] = useUpdateFileMutation()

  const handleDisplayFile = async () => {
    if (!complianceFileInfo) {
      downloadFileCallback(null, fileType)
      return
    }
    const {
      data: { location },
    } = await downloadFileRequest({
      accountId,
      fileId: complianceFileInfo.fileId,
    }).unwrap()
    const response = await downloadFile(location)
    if (response.data) {
      downloadFileCallback(response.data, fileType)
    }
  }

  const resetFile = () => {
    setErrorMessage('')
  }
  const removeFile = () => {
    if (fileInputRef && fileInputRef.current && fileInputRef.current.value) {
      resetFile()
      fileInputRef.current.value = ''
    }
  }

  const triggerUpload = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    if (!fileInputRef || !fileInputRef.current) {
      return
    }
    removeFile()
    fileInputRef.current.click()
  }

  const fileUpload = async (evt: React.ChangeEvent<HTMLInputElement>) => {
    const file = evt.target.files ? evt.target.files[0] : null
    if (!file) {
      resetFile()
      return
    }
    if (file.size > maxFileSize) {
      removeFile()
      setErrorMessage(`File is too big. Maximum allowed size: ${maxFileSize}`)
      return
    }
    try {
      const {
        data: {
          fileUploadUrl,
          fileUploadParameters,
          fileInfo: { fileId },
        },
      } = await uploadFileRequest({
        accountId,
        body: {
          fileName: file.name,
          fileSize: file.size,
          contentType: file.type,
          kind: FileKind.COMPLIANCE_ASSET,
        },
      }).unwrap()
      await uploadFile({ uploadUrl: fileUploadUrl, uploadParameters: fileUploadParameters, file }).unwrap()
      await updateFile({ accountId, fileId, body: { status: FileStatus.UPLOADED } }).unwrap()
      uploadFileCallback(fileId, fileType, type)
    } finally {
      removeFile()
    }
  }

  const resetComplianceFile = () => {
    resetFileCallback(fileType, type)
  }

  const anyLoading = isLoading || uploadFileIsLoading || uploadFileRequestIsLoading || updateFileIsLoading

  return (
    <>
      <div
        className={classnames('p-2 rounded zocks-bg-color-gray-100 d-flex flex-row justify-content-between zocks-border-gray-200', {
          'zocks-border-color-danger': !!errorMessage,
        })}
      >
        <div className='d-flex gap-4'>
          <Button
            variant='dark'
            style={{ backgroundImage: `url(${buttonBackgroundImage})` }}
            className='p-0 square-button-medium button-custom-bg'
            onClick={handleDisplayFile}
          >
            <i className='bi bi-search'></i>
          </Button>
          <div>
            {complianceFileInfo && (
              <div>
                <span className='bold'>{complianceFileInfo.fileName}</span>
              </div>
            )}
            {complianceFileInfo?.fileSize && <div>{humanFileSize(complianceFileInfo.fileSize)}</div>}
          </div>
        </div>

        <Form.Control ref={fileInputRef} id={`${fileType}-file`} type='file' accept={supportedMimeTypes} onChange={fileUpload} hidden={true} />
        <ScopedBlock
          scope={{
            scope: 'compliance_notice:update',
            superScope: ['compliance_notice:admin'],
          }}
          missingScopeAction={MissingScopeAction.DISABLE}
        >
          <Button
            disabled={!uploadEnabled || anyLoading}
            variant='grayGhost'
            className='zocks-border-gray-500 p-0 square-button-medium'
            onClick={triggerUpload}
          >
            <i
              className={classnames('bi bi-arrow-repeat', {
                'spin-faster': anyLoading,
              })}
            />
          </Button>
        </ScopedBlock>
      </div>
      <div className='mt-2'>
        <span className='text-danger'>{errorMessage}</span>
        {!!complianceFileInfo && (
          <ScopedBlock
            scope={{
              scope: 'compliance_notice:update',
              superScope: ['compliance_notice:admin'],
            }}
            missingScopeAction={MissingScopeAction.DISABLE}
          >
            <Button
              disabled={!restoreEnabled || anyLoading}
              onClick={resetComplianceFile}
              variant='link'
              className='text-decoration-underline button-link-small'
            >
              Restore to default
            </Button>
          </ScopedBlock>
        )}
      </div>
    </>
  )
}
